/**
 * CRM Multi-Compagnia · Lista Sinistri
 */
import { useState } from "react";
import { ShieldAlert, Plus } from "lucide-react";
import {
  api, useApi, DataTable, Spinner, ErrorBox, Btn, Modal, Field, TextInput, Select,
  StatusBadge, RamoChip, RAMI_LABEL, toast, fmtDate, fmtEUR
} from "@/components/crm-multi/shared";

const RAMI_LIST = Object.keys(RAMI_LABEL);
const STATI = ["aperto", "in_istruttoria", "liquidato", "respinto", "chiuso"];

export default function CrmMultiSinistriPage() {
  const [stato, setStato] = useState("");
  const list = useApi(() => api.get("/claims", { stato: stato || undefined, limit: 100 }), [stato]);
  const clienti = useApi(() => api.get("/customers", { limit: 500 }));
  const compagnie = useApi(() => api.get("/companies", { limit: 200 }));
  const polizze = useApi(() => api.get("/policies", { limit: 500 }));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ numero_sinistro: "", customer_id: "", company_id: "", policy_id: "", ramo: "rca", data_sinistro: "", data_denuncia: new Date().toISOString().slice(0, 10), descrizione: "", importo_richiesto_eur: 0 });

  const cliMap = Object.fromEntries((clienti.data?.items || []).map(c => [c.id, c.anagrafica]));
  const compMap = Object.fromEntries((compagnie.data?.items || []).map(c => [c.id, c.nome]));

  const onSave = async (e) => {
    e.preventDefault();
    try { await api.post("/claims", form); toast("Sinistro registrato", "success"); setOpen(false); list.refresh(); }
    catch (er) { toast(JSON.stringify(er?.response?.data?.detail || er.message), "error"); }
  };

  return (
    <div className="space-y-4" data-testid="cm-sinistri-page">
      <header className="flex justify-between items-center gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> Sinistri</h2>
          <p className="text-sm text-zinc-500">{list.data?.total ?? 0} sinistri</p>
        </div>
        <div className="flex gap-2">
          <Select value={stato} onChange={(e) => setStato(e.target.value)} className="md:w-44">
            <option value="">Tutti gli stati</option>
            {STATI.map(s => <option key={s}>{s}</option>)}
          </Select>
          <Btn variant="primary" onClick={() => setOpen(true)} data-testid="cm-sinistri-new"><Plus className="w-4 h-4" /> Nuovo</Btn>
        </div>
      </header>

      <ErrorBox error={list.error} />
      {list.loading ? <Spinner /> : (
        <DataTable
          testid="cm-sinistri-table"
          columns={[
            { key: "numero_sinistro", label: "Nr.", render: r => <code className="text-xs">{r.numero_sinistro}</code> },
            { key: "compagnia", label: "Compagnia", render: r => <span className="text-xs">{compMap[r.company_id] || "?"}</span> },
            { key: "cliente", label: "Cliente", render: r => {
              const a = cliMap[r.customer_id];
              return <span className="text-xs">{a?.tipo === "azienda" ? a.ragione_sociale : `${a?.nome || ""} ${a?.cognome || ""}`}</span>;
            }},
            { key: "ramo", label: "Ramo", render: r => <RamoChip ramo={r.ramo} /> },
            { key: "data_sinistro", label: "Data", render: r => fmtDate(r.data_sinistro) },
            { key: "importo_richiesto_eur", label: "Richiesto", render: r => <span className="font-mono text-xs">{fmtEUR(r.importo_richiesto_eur)}</span> },
            { key: "stato", label: "Stato", render: r => <StatusBadge tipo="sinistro" valore={r.stato} /> },
          ]}
          rows={list.data?.items || []}
          emptyTitle="Nessun sinistro"
        />
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Nuovo sinistro" maxWidth="max-w-xl">
        <form onSubmit={onSave} className="grid grid-cols-2 gap-3">
          <Field label="Numero sinistro" required><TextInput required value={form.numero_sinistro} onChange={(e) => setForm({ ...form, numero_sinistro: e.target.value })} /></Field>
          <Field label="Ramo"><Select value={form.ramo} onChange={(e) => setForm({ ...form, ramo: e.target.value })}>{RAMI_LIST.map(r => <option key={r} value={r}>{RAMI_LABEL[r]}</option>)}</Select></Field>
          <Field label="Cliente" required>
            <Select required value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
              <option value="">Seleziona...</option>
              {(clienti.data?.items || []).map(c => <option key={c.id} value={c.id}>{c.anagrafica?.tipo === "azienda" ? c.anagrafica.ragione_sociale : `${c.anagrafica?.nome} ${c.anagrafica?.cognome}`}</option>)}
            </Select>
          </Field>
          <Field label="Compagnia" required>
            <Select required value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })}>
              <option value="">Seleziona...</option>
              {(compagnie.data?.items || []).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </Select>
          </Field>
          <Field label="Polizza" required>
            <Select required value={form.policy_id} onChange={(e) => setForm({ ...form, policy_id: e.target.value })}>
              <option value="">Seleziona...</option>
              {(polizze.data?.items || []).filter(p => !form.customer_id || p.customer_id === form.customer_id).map(p => <option key={p.id} value={p.id}>{p.numero_polizza}</option>)}
            </Select>
          </Field>
          <Field label="Data sinistro" required><TextInput required type="date" value={form.data_sinistro} onChange={(e) => setForm({ ...form, data_sinistro: e.target.value })} /></Field>
          <Field label="Data denuncia" required><TextInput required type="date" value={form.data_denuncia} onChange={(e) => setForm({ ...form, data_denuncia: e.target.value })} /></Field>
          <Field label="Importo richiesto €"><TextInput type="number" step="0.01" value={form.importo_richiesto_eur} onChange={(e) => setForm({ ...form, importo_richiesto_eur: parseFloat(e.target.value || 0) })} /></Field>
          <div className="col-span-2"><Field label="Descrizione"><textarea className="w-full px-3 py-1.5 text-sm border border-zinc-300 rounded" rows={3} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} /></Field></div>
          <div className="col-span-2 flex justify-end gap-2 pt-3 border-t"><Btn variant="outline" type="button" onClick={() => setOpen(false)}>Annulla</Btn><Btn variant="primary" type="submit">Salva</Btn></div>
        </form>
      </Modal>
    </div>
  );
}
